import { useRef, useState } from 'react';
import type { MutableRefObject, ReactNode } from 'react';
import { getKsdmConfig } from 'helpers/index';
import { useCurrentUser } from 'hooks/useDashboardData';
import {
  BUG_AREAS,
  addAttachments,
  bugAttachmentRules,
  formatFileSize,
} from 'app/services/bugReportApi';

/**
 * The pieces Report a Bug (`BugReportPopup.tsx`) and Request a Feature
 * (`FeatureRequestPopup.tsx`) share. Both send to the same pipeline with the
 * same captured context and attachment rules, so the parts of the dialog that
 * stand for those — the context strip, Your email, Area, the file row, the discard
 * confirmation and the thank-you card — are one implementation, styled by one
 * stylesheet (`django/static/css/common/sass/ui/popup/_feedback_form.scss`).
 * What differs between the two (their own fields, wording, endpoint) stays in
 * each popup.
 */

/** Size a textarea to its content, so a field opens at a compact height and
 *  grows a row at a time as it is typed in, instead of sitting four rows deep
 *  with one line in it.
 *
 *  `height: auto` first: without it `scrollHeight` is measured against the
 *  height we set last time and the box can only ever grow, so deleting a line
 *  would leave the gap behind. The floor and the ceiling are
 *  `_feedback_form.scss`'s `min-height`/`max-height` — past the ceiling the
 *  element's own `overflow-y` takes over and it scrolls again. Used as a ref
 *  callback as well as on change: the form stays mounted behind the discard
 *  confirmation, so it is re-measured when that is dismissed.
 *
 *  ★ `+ chrome`, not a bare `scrollHeight`: these textareas are
 *  `box-sizing: border-box` with a 1px bottom border (_ui.input.scss), so a
 *  height of exactly `scrollHeight` gives the content one pixel less than it
 *  measured — `overflow-y: auto` then draws a scrollbar on a box that just
 *  resized itself to fit, which is most obvious on the now-shallow first field.
 *  `offsetHeight - clientHeight` is that border, read while the height is still
 *  `auto`. */
export const autoGrow = (el: HTMLTextAreaElement | null): void => {
  if (!el) return;
  el.style.height = 'auto';
  const chrome = el.offsetHeight - el.clientHeight;
  el.style.height = `${el.scrollHeight + chrome}px`;
};

/** The signed-in account's email from the server-injected bootstrap config —
 *  available before (and without) the /users/<id> fetch. */
const bootEmail = (): string => {
  const user = getKsdmConfig()?.user as { email?: unknown } | null | undefined;
  return typeof user?.email === 'string' ? user.email : '';
};

/**
 * First human-readable message out of an error response.
 *
 * ★ Not every failure comes from Django. nginx answers an oversize body with an
 * HTML 413 and the ALB answers a slow request with an HTML 504, and
 * `apiConfig.ts` uses the default `json` response handler — so RTK Query hands
 * those back as `{status: 'PARSING_ERROR', originalStatus, data: '<html>…'}`.
 * Returning `data` on sight would print that page's markup into the error line
 * (React escapes it, so it is ugly rather than unsafe). Non-JSON bodies are
 * therefore turned into our own sentence, keyed off `originalStatus`.
 */
/** The first string in a DRF error body, however deep. A list field's child
 *  errors come back nested by index (`{attachment: {0: ['…']}}`), and stopping
 *  at the top level turned those into "please try again" for a file that will
 *  fail every time. */
const firstMessage = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    for (const item of Object.values(value as Record<string, unknown>)) {
      const found = firstMessage(item);
      if (found) return found;
    }
  }
  return null;
};

export const feedbackErrorMessage = (
  error: unknown,
  { generic, tooMany }: { generic: string; tooMany: string }
): string => {
  const err = (error ?? {}) as { status?: number | string; originalStatus?: number; data?: unknown };
  if (err.status === 'PARSING_ERROR') {
    if (err.originalStatus === 413) {
      const { maxTotalBytes } = bugAttachmentRules();
      return `The attachments are too large to upload. The limit is ${Math.round(
        maxTotalBytes / 1048576
      )} MB in total.`;
    }
    return generic;
  }

  const { data } = err;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    if (typeof record.error === 'string') return record.error;
    const found = firstMessage(record);
    if (found) return found;
  }
  if (err.status === 429) return tooMany;
  return generic;
};

/** The Area a form opens on: the page the user is on (GlobalHeader's `appName`,
 *  which is also the server's `area` value), or `other`. */
export const defaultArea = (appName?: string): string =>
  BUG_AREAS.some((a) => a.value === appName) ? (appName as string) : 'other';

/**
 * The open/close rules both forms follow.
 *
 * `requestClose` is the only way a popup closes while there is something to
 * lose. Esc reaches it too (usePopupKeys clicks `button[name="cancel"]`), and
 * Esc is easy to hit by accident — it is also how you cancel an IME
 * composition. Losing a paragraph of carefully written text to that is the kind
 * of thing that stops someone using the form next time.
 *
 * While the POST is in flight, closing is refused outright rather than
 * confirmed: the request is not cancelled by unmounting, so the submission
 * would be filed with the user never seeing its reference — and they would
 * reasonably file it again.
 */
export const useCloseGuard = ({
  isLoading,
  hasDraft,
  done,
  onClose,
}: {
  isLoading: boolean;
  hasDraft: boolean;
  done: boolean;
  onClose: () => void;
}) => {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const requestClose = () => {
    if (isLoading) return;
    if (hasDraft && !done) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };
  const keepEditing = () => setConfirmDiscard(false);
  const discard = () => {
    // Same rule as requestClose: never close while the POST is in flight, or
    // the user loses the reference to a submission that did get sent.
    if (isLoading) return;
    setConfirmDiscard(false);
    onClose();
  };
  return { confirmDiscard, requestClose, keepEditing, discard };
};

/**
 * The optional files, each validated before any is uploaded.
 *
 * Without the check the user waits out the whole upload to be told no: the
 * serializer rejects anything over a cap, and nginx kills anything over its
 * `client_max_body_size` with an HTML 413 while the browser is still writing
 * the body. The server remains the authority — this is the version of the
 * answer that arrives in time to be useful.
 *
 * The input is cleared after every pick: the list `AttachmentField` shows is
 * the record of what will be sent, so files accumulate across picks
 * (screenshots from two folders), and re-picking a file just removed still
 * fires `change` — the browser compares values, not identity.
 */
export const useAttachments = (setReqError: (message: string) => void) => {
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const pick = (picked: FileList | null) => {
    const { files, errors } = addAttachments(attachments, Array.from(picked ?? []));
    if (fileInputRef.current) fileInputRef.current.value = '';
    setAttachments(files);
    setReqError(errors.join(' '));
  };

  const remove = (index: number) => {
    setAttachments((files) => files.filter((_, i) => i !== index));
    setReqError('');
  };

  return { attachments, fileInputRef, pick, remove };
};

/**
 * The read-only strip under the heading: the page the submission will carry
 * and the build it was made on, both collected by `appendCapturedContext`.
 *
 * Read on every render rather than pinned at open: the form sends
 * `window.location.href` as it is when Send is clicked, and a strip that said
 * something else would be a small lie about what was filed.
 */
export const FeedbackContext = () => {
  const pageLabel = `${window.location.pathname}${window.location.search}`;
  const softwareVersion = getKsdmConfig()?.versions?.software ?? '';
  return (
    <div className="popup-content-part feedback-context">
      <div>
        <span className="feedback-context-label">Page :</span>
        <span className="feedback-context-value">{pageLabel}</span>
      </div>
      {softwareVersion && (
        <div>
          <span className="feedback-context-label">Version :</span>
          <span className="feedback-context-value">{softwareVersion}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Email Address is SHOWN, not asked for: the signed-in account's address, which
 * the pipeline sends a receipt to and support replies to. (Until 2026-10-23 it
 * was CC'd on support's own mail; that mail now carries internal links, so the
 * reporter gets a separate receipt — ksdm-infra docs/BUG_REPORTS.md.) It was
 * briefly an editable field on the bug form, and that is a thing to leave alone — a free-text destination
 * turns the pipeline into a way for any logged-in user to have text they wrote
 * delivered to any address they like, from a sender carrying Kinetix's
 * reputation. The case it served (file for a colleague, copy a team inbox) is
 * what forwarding is for. Displaying it still earns its place: the user can see
 * where the answer will go, and a stale address in their profile becomes
 * visible at the moment it matters.
 *
 * `.output` is the app's read-only-value class, the same one the edit-profile
 * popup uses for Access Type and Role.
 */
export const EmailField = ({
  id,
  hint,
  noEmailHint,
}: {
  id: string;
  /** Under the address: who replies there. */
  hint: string;
  /** Instead of `hint`, for an account with no address. */
  noEmailHint: string;
}) => {
  // Already in the RTK Query cache — GlobalHeader, which renders these popups,
  // reads the same query for the user-menu initials. Used for the fresher
  // address (the Edit Profile popup can change it in this same session) with the
  // bootstrap config as the value that is there on the first paint.
  const { data: currentUser } = useCurrentUser();
  const accountEmail = currentUser?.email || bootEmail();
  return (
    <div className="feedback-field">
      <span className="label">Your email</span>
      <div className="feedback-control">
        <span className="output" id={id}>
          {accountEmail || '—'}
        </span>
        <span className="feedback-field-hint">{accountEmail ? hint : noEmailHint}</span>
      </div>
    </div>
  );
};

export const AreaField = ({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="feedback-field">
    <label className="label" htmlFor={id}>
      Area
    </label>
    <div className="feedback-control">
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {BUG_AREAS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  </div>
);

/**
 * The minimum under a required field, stated rather than left to be
 * discovered, because the only other sign of it is Send staying dead — which
 * looks like a broken button, not an unfinished field. It turns red once there
 * is something typed and it is still short: an untouched form is not yet
 * wrong, and reading the whole dialog in red before touching it is what makes
 * people stop reading the hints at all. Callers compare `trim()`med lengths on
 * both sides so the red text and the button never disagree about the same
 * value.
 */
export const MinLengthHint = ({ min, length }: { min: number; length: number }) => (
  <span
    className={`feedback-field-hint${length > 0 && length < min ? ' feedback-field-hint-error' : ''}`}
  >
    Minimum {min} characters
  </span>
);

export const AttachmentField = ({
  id,
  label,
  attachments,
  fileInputRef,
  onPick,
  onRemove,
}: {
  id: string;
  label: string;
  attachments: File[];
  fileInputRef: MutableRefObject<HTMLInputElement | null>;
  onPick: (files: FileList | null) => void;
  onRemove: (index: number) => void;
}) => {
  // The caps come from this server's settings, like the checks in
  // `addAttachments` — a hint that disagrees with what the form will accept is
  // worse than no hint.
  const { maxAttachmentBytes, maxVideoBytes, maxAttachments, maxTotalBytes } =
    bugAttachmentRules();
  const attachmentMb = Math.round(maxAttachmentBytes / 1048576);
  const videoMb = Math.round(maxVideoBytes / 1048576);
  const totalMb = Math.round(maxTotalBytes / 1048576);
  return (
    <div className="feedback-field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <div className="feedback-control feedback-attachment">
        {/* Hidden, and opened by Add files: the list below is what will be
            sent, and a native control that reset to "No file chosen" after
            every pick would contradict it. `accept` is spelled out rather than
            `image/*` + `video/*`: those wildcards let the picker offer a .svg
            or an .avi that the server then rejects, and after a 40 MB upload is
            the worst moment to find that out. Keep in step with
            ALLOWED_ATTACHMENT_EXTENSIONS in api/rest_bug_report/serializers.py. */}
        <input
          id={id}
          ref={fileInputRef}
          className="feedback-file-input"
          type="file"
          multiple
          tabIndex={-1}
          accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.log,.csv,.json,.zip,.mp4,.mov,.webm,.m4v,.mkv"
          onChange={(e) => onPick(e.target.files)}
        />
        {attachments.length > 0 && (
          <ul className="feedback-file-list">
            {attachments.map((file, i) => (
              <li key={`${file.name}-${file.size}-${file.lastModified}`}>
                <span className="feedback-file-name" title={file.name}>
                  {file.name}
                </span>
                <span className="feedback-file-size">{formatFileSize(file.size)}</span>
                <button
                  type="button"
                  className="feedback-file-remove"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => onRemove(i)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        {attachments.length < maxAttachments && (
          <button
            type="button"
            className="feedback-file-add"
            onClick={() => fileInputRef.current?.click()}
          >
            {attachments.length ? 'Add more files' : 'Add files'}
          </button>
        )}
        <span className="feedback-file-hint">
          Optional. Up to {maxAttachments} files, {totalMb} MB in total: images, PDF, log, CSV
          or zip up to {attachmentMb} MB each, or screen recordings (MP4, MOV, WebM, MKV) up to{' '}
          {videoMb} MB.
        </span>
      </div>
    </div>
  );
};

/**
 * Rendered OVER the form, not instead of it — the form stays mounted.
 *
 * Returning a different tree once unmounted the uncontrolled
 * `<input type="file">`, so Keep editing brought it back empty while the file
 * was still in state: the control said "No file chosen" and Send uploaded the
 * file anyway. The picked files are a list in state now, but keeping the form
 * mounted is still what makes Keep editing put the user back exactly where
 * they were.
 *
 * It is the LAST `.popup-wrapper.active` in the DOM, so usePopupKeys acts on
 * this one, and Keep editing is the button named `cancel` — Esc on this step is
 * the safe answer rather than the destructive one. On pages that use blanket
 * mode (Sizer, User Settings) Esc clicks EVERY active popup's `cancel`, in DOM
 * order: the form's Cancel goes first and only re-sets `confirmDiscard`, which
 * is already true, then Keep editing clears it — the same safe outcome.
 *
 * Keep editing takes focus when the confirm opens. Esc clicks Cancel without
 * moving focus, so otherwise it stays on whatever the user left it on behind
 * the confirm — Send included — and Enter would send the form while this
 * dialog says nothing has been sent.
 */
export const DiscardConfirm = ({
  title,
  children,
  onKeep,
  onDiscard,
}: {
  title: string;
  children: ReactNode;
  onKeep: () => void;
  onDiscard: () => void;
}) => (
  <div className="popup-wrapper feedback-popup feedback-done feedback-discard active">
    <div className="popup-content">
      <h2 className="popup-heading">{title}</h2>
      <div className="popup-content-part">{children}</div>
      {/* `.popup-control-buttons button` is `float: right`, so the FIRST in the
          DOM lands rightmost — where this app puts the primary action. Keep
          editing is the primary here; discarding is the exception. */}
      <div className="popup-control-buttons">
        <button className="btn highlighted-btn" type="button" name="cancel" autoFocus onClick={onKeep}>
          Keep editing
        </button>
        <button className="btn not-highlighted-btn" type="button" onClick={onDiscard}>
          Discard
        </button>
      </div>
    </div>
  </div>
);

/** The card that replaces the form once the submission is in. */
export const FeedbackDone = ({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) => (
  <div id="popup-content" className="popup-wrapper feedback-popup feedback-done active">
    <div className="popup-content">
      <h2 className="popup-heading">{title}</h2>
      <div className="popup-content-part">{children}</div>
      <div className="popup-control-buttons">
        <button className="btn highlighted-btn" type="button" name="cancel" autoFocus onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  </div>
);
